import {useEffect, useRef, useState} from 'react';

import {Action, view} from '@sail/ui';

import {
  SpeakerButton,
  useAnalytics,
  useTheme,
  useWidget,
} from '@stripe-internal/conversations-ui';
import {
  useSetMinimizedHeight,
  useTriggerParams,
  useVisibility,
} from '@stripe-internal/conversations-assistant';

import {CreateStripeAccountLink} from './CreateStripeAccountLink';
import {DotComCloseButton} from './DotComCloseButton';
import {dotComColors} from '../../theme/dotCom';
import {DotComHeaderTitle} from './DotComHeaderTitle';
import {DotComMinimizeButton} from './DotComMinimizeButton';
import {useDotComCsf} from '../../dotComBehavior';

type Props = {
  isCSAT: boolean;
};

export function DotComHeader({isCSAT}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const setMinimizedHeight = useSetMinimizedHeight();
  const {visibility, open, minimize, close} = useVisibility();
  const analytics = useAnalytics();
  const theme = useTheme();
  const isChatEmbeddedOnSalesForm = useDotComCsf();

  const triggerParams = useTriggerParams();
  const isSelfServeRedirect =
    triggerParams?.triggerPoint === 'dot_com_embedded_csf_self_serve';
  const isPricingChoosePathway =
    triggerParams?.triggerPoint === 'dot_com_pricing_choose_pathway';

  const {header} = useWidget();
  const [showHeader, setShowHeader] = useState<boolean>(header.isVisible());
  const [headerDisabled, setHeaderDisabled] = useState<boolean>(
    header.isDisabled(),
  );

  useEffect(() => {
    const sub = header.visibilityObservable().subscribe(setShowHeader);
    return () => sub.unsubscribe();
  }, [header]);

  useEffect(() => {
    const sub = header.disabledObservable().subscribe(setHeaderDisabled);
    return () => sub.unsubscribe();
  }, [header]);

  useEffect(() => {
    if (ref.current) {
      setMinimizedHeight(ref.current.offsetHeight);
    }
  }, [setMinimizedHeight, showHeader]);

  const toggleVisibility = () => {
    if (headerDisabled) {
      return;
    }

    analytics.action('toggle_minimize_header_clicked');
    if (visibility === 'open') {
      minimize();
    } else {
      open();
    }
  };

  if (!showHeader) {
    return null;
  }

  const headerContent = (
    <view.div
      css={{
        userSelect: 'none',
        backgroundColor: theme.brandColor,
        paddingY: 'small',
        paddingX: 'medium',
        stack: 'x',
        alignY: 'center',
        maxHeight: '42px',
        flexGrow: '0',
      }}
    >
      {headerDisabled ? (
        <view.div
          data-testid="conversations-widget-header-disabled-dimmer"
          css={{
            position: 'absolute',
            zIndex: 10,
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            opacity: '0.3',
            backgroundColor: '#000000',
          }}
        />
      ) : null}

      {isChatEmbeddedOnSalesForm ? (
        <DotComHeaderTitle
          isCSAT={isCSAT}
          isCSF={isChatEmbeddedOnSalesForm}
          isCSFLLM={isSelfServeRedirect}
          isPricingChoosePathway={isPricingChoosePathway}
        />
      ) : (
        <Action
          css={{
            flexGrow: '1',
            minWidth: '0',
          }}
          tabIndex={-1}
          disabled={headerDisabled}
          onPress={toggleVisibility}
        >
          <DotComHeaderTitle
            isCSAT={isCSAT}
            isCSF={isChatEmbeddedOnSalesForm}
            isCSFLLM={isSelfServeRedirect}
            isPricingChoosePathway={isPricingChoosePathway}
          />
        </Action>
      )}

      <view.div
        css={{
          stack: 'x',
          alignY: 'center',
          alignX: 'end',
          flex: 'inherit',
          gap: '12px',
          height: 'fill',
          paddingLeft: '10px',
        }}
      >
        <SpeakerButton
          disabled={headerDisabled}
          color={dotComColors.brand800}
          hoverColor={dotComColors.brand900}
          tooltipEnabled={false}
        />
        {isChatEmbeddedOnSalesForm ? null : (
          <DotComMinimizeButton
            visibility={visibility}
            onOpen={open}
            onMinimize={minimize}
            disabled={headerDisabled}
            color={dotComColors.brand800}
            hoverColor={dotComColors.brand900}
          />
        )}
        {isSelfServeRedirect ? (
          <CreateStripeAccountLink />
        ) : (
          <DotComCloseButton
            visibility={visibility}
            onClose={close}
            disabled={headerDisabled}
            color={dotComColors.brand800}
            hoverColor={dotComColors.brand900}
            tooltipEnabled={false}
          />
        )}
      </view.div>
    </view.div>
  );

  return (
    <view.div
      ref={ref}
      data-testid="conversations-widget-header"
      css={{
        stack: 'y',
        flexGrow: '0 !important',
        position: 'relative',
      }}
    >
      {headerContent}

      <view.div
        css={{
          height: '2px',
          background: theme.headerBarGradient,
        }}
      />
    </view.div>
  );
}
